/*
  Workload-Berechnung für Mitarbeitende im Monatsraster.

  Die verbindliche Regel für geplante Stunden steht im Backend in
  databases/hours_resolution.py. Hier liegt die einzige Spiegelung im Client —
  sie wird gebraucht, weil der Schichtplan die Summe beim Editieren live
  fortschreibt, bevor der Server geantwortet hat. Liefert der Server einen Wert
  mit (plannedMinutes / plannedMinutesPerDay), gewinnt dieser; gerechnet wird
  nur, wenn er fehlt.

  Beide Seiten werden gegen dieselbe Fixture geprüft
  (backend_tests/fixtures/planned_hours.json) — Änderungen gehören an beide
  Stellen.
*/

import { parseDateValue } from "../date/month";
import { getShiftDayKeys } from "./selectors";
import { countUserHolidayDays, getUserHolidayDayKeys } from "./userHolidays";

const DEFAULT_WORKING_DAYS_PER_WEEK = 5;
const FULL_CIRCLE_DEGREES = 360;
const MILLISECONDS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const WORKLOAD_HOUR_EPSILON = 0.05;

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function minutesFromHours(hours) {
  return Math.max(0, Math.round((toFiniteNumber(hours) || 0) * MINUTES_PER_HOUR));
}

function minutesToHours(minutes) {
  return Math.round(((minutes || 0) / MINUTES_PER_HOUR) * 100) / 100;
}

export function isWorkingDay(date, workingDaysPerWeek) {
  const weekday = date.getDay();
  const normalizedWorkingDays = Math.max(1, Math.min(7, Number(workingDaysPerWeek || DEFAULT_WORKING_DAYS_PER_WEEK)));

  if (normalizedWorkingDays >= 7) return true;
  if (normalizedWorkingDays === 6) return weekday !== 0;
  return weekday >= 1 && weekday <= normalizedWorkingDays;
}

function parseTimeMinutes(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * MINUTES_PER_HOUR + minutes;
}

function getTimeMinutesFromDateTime(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  return (parsed.getHours() * MINUTES_PER_HOUR) + parsed.getMinutes();
}

/*
  Pausenlänge in Minuten — der Typ sagt, welches Feld gilt: "duration" nimmt
  die Dauer (auch 0), "fixed" die Uhrzeiten. Fehlt das maßgebende Feld, greift
  das andere.
*/
function getBreakMinutes(breakItem) {
  const durationMinutes = toFiniteNumber(breakItem?.durationMinutes ?? breakItem?.duration_minutes);
  const normalizedDuration = durationMinutes === null ? null : Math.max(0, Math.round(durationMinutes));

  const start = parseDateValue(breakItem?.start ?? breakItem?.start_at);
  const end = parseDateValue(breakItem?.end ?? breakItem?.end_at);
  let timedMinutes = null;
  if (start && end && end.getTime() > start.getTime()) {
    timedMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / MILLISECONDS_PER_MINUTE));
  } else {
    const startMinutes = parseTimeMinutes(breakItem?.startTime ?? breakItem?.start_time);
    const endMinutes = parseTimeMinutes(breakItem?.endTime ?? breakItem?.end_time);
    if (startMinutes != null && endMinutes != null && endMinutes > startMinutes) {
      timedMinutes = endMinutes - startMinutes;
    }
  }

  const breakType = String(breakItem?.type ?? breakItem?.breakType ?? breakItem?.break_type ?? "").trim().toLowerCase();
  if (breakType === "duration") {
    return normalizedDuration === null ? (timedMinutes || 0) : normalizedDuration;
  }

  return timedMinutes === null ? (normalizedDuration || 0) : timedMinutes;
}

function getTotalBreakMinutes(shift) {
  return (Array.isArray(shift?.breaks) ? shift.breaks : []).reduce(
    (total, breakItem) => total + getBreakMinutes(breakItem),
    0
  );
}

/*
  Sicheres Ende für kaputt gespeicherte Standardschichten: ein älterer
  Editor-Pfad behielt das Enddatum des Folgetags, wenn eine Übernachtschicht
  auf eine Tagesvorlage umgestellt wurde. Ohne die Vorlage im Client bleibt es
  bei der gespeicherten Dauer — den Fall repariert dann der Serverwert.
*/
function getShiftGrossMinutes(shift) {
  const start = parseDateValue(shift?.start);
  const end = parseDateValue(shift?.end);
  if (!start || !end) return 0;

  const grossMinutes = Math.round((end.getTime() - start.getTime()) / MILLISECONDS_PER_MINUTE);
  return grossMinutes > 0 ? grossMinutes : 0;
}

export function getShiftPlannedMinutes(shift) {
  const serverMinutes = toFiniteNumber(shift?.plannedMinutes);
  if (serverMinutes !== null) return Math.max(0, Math.round(serverMinutes));

  return Math.max(0, getShiftGrossMinutes(shift) - getTotalBreakMinutes(shift));
}

function isVacationShift(shift, vacationAbsenceIds) {
  if (shift?.entryType !== "absence") return false;
  return vacationAbsenceIds.has(String(shift?.absenceId || ""));
}

function buildAbsenceOptionsById(absenceOptions) {
  return (Array.isArray(absenceOptions) ? absenceOptions : []).reduce((accumulator, option) => {
    const id = String(option?.id || "").trim();
    if (!id) return accumulator;
    accumulator.set(id, option);
    return accumulator;
  }, new Map());
}

function countWorkingDays(days, workingDaysPerWeek) {
  return (Array.isArray(days) ? days : []).filter((day) => {
    const date = parseDateValue(day?.date);
    return date ? isWorkingDay(date, workingDaysPerWeek) : false;
  }).length;
}

function getVisibleWorkingDayKeys(dayKeys, visibleDaysByKey, workingDaysPerWeek) {
  return (Array.isArray(dayKeys) ? dayKeys : []).filter((dateKey) => {
    const visibleDay = visibleDaysByKey.get(dateKey);
    if (!visibleDay) return false;
    const visibleDate = parseDateValue(visibleDay?.date || dateKey);
    return visibleDate ? isWorkingDay(visibleDate, workingDaysPerWeek) : false;
  });
}

export function getEmployeeDailyTargetHours(employee, workingDaysPerWeek) {
  const weeklyHours = Number(employee?.weeklyHours || 0);
  const normalizedWorkingDays = Math.max(1, Math.min(7, Number(workingDaysPerWeek || DEFAULT_WORKING_DAYS_PER_WEEK)));
  return weeklyHours > 0 ? weeklyHours / normalizedWorkingDays : 0;
}

function absenceCountsAsWorkTime(shift, absenceOptionsById) {
  if (shift?.entryType !== "absence") return false;

  const absenceId = String(shift?.absenceId || "").trim();
  if (!absenceId) return false;

  const absenceOption = absenceOptionsById.get(absenceId);
  return absenceOption?.countsAsWorkTime === true;
}

/*
  Minuten, die ein einzelner Absenztag anrechnet.

  Bei erfassten Uhrzeiten gewinnt die Uhrzeitspanne. Ganztägig zählt der im
  Eintrag eingefrorene Wert; fehlt er, bleibt es beim individuellen Tagessoll.
*/
export function getAbsencePlannedMinutesPerDay(shift, dailyTargetHours) {
  const serverMinutes = toFiniteNumber(shift?.plannedMinutesPerDay);
  if (serverMinutes !== null) return Math.max(0, Math.round(serverMinutes));

  if (!shift?.isAllDay) {
    const startMinutes = getTimeMinutesFromDateTime(shift?.start);
    const endMinutes = getTimeMinutesFromDateTime(shift?.end);
    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) return 0;
    return endMinutes - startMinutes;
  }

  const frozenHours = toFiniteNumber(shift?.absenceHoursPerDay);
  if (frozenHours !== null) return minutesFromHours(frozenHours);

  return minutesFromHours(dailyTargetHours);
}

/*
  Alte Signatur für Aufrufer, die mit Stunden rechnen (Editor-Vorschau).
*/
export function getAbsenceHoursPerDay(shift, dailyTargetHours) {
  return getAbsencePlannedMinutesPerDay(shift, dailyTargetHours) / MINUTES_PER_HOUR;
}

function getWorkTimeAbsenceMinutes(shift, visibleDaysByKey, workingDaysPerWeek, dailyTargetHours, skipDayKeys) {
  const workingDayKeys = getVisibleWorkingDayKeys(getShiftDayKeys(shift), visibleDaysByKey, workingDaysPerWeek).filter(
    (dateKey) => !skipDayKeys || !skipDayKeys.has(dateKey)
  );
  if (!workingDayKeys.length) return 0;

  return workingDayKeys.length * getAbsencePlannedMinutesPerDay(shift, dailyTargetHours);
}

function getEntryPlannedMinutes(
  shift,
  absenceOptionsById,
  visibleDaysByKey,
  workingDaysPerWeek,
  dailyTargetHours,
  skipDayKeys
) {
  if (shift?.entryType !== "absence") return getShiftPlannedMinutes(shift);

  // Der Serverwert kennt die Arbeitszeitanrechnung bereits; ohne ihn
  // entscheidet die Absenzart im Client.
  const hasServerValue = toFiniteNumber(shift?.plannedMinutesPerDay) !== null;
  if (!hasServerValue && !absenceCountsAsWorkTime(shift, absenceOptionsById)) return 0;

  return getWorkTimeAbsenceMinutes(shift, visibleDaysByKey, workingDaysPerWeek, dailyTargetHours, skipDayKeys);
}

function countWorkingDayKeys(dayKeys, workingDaysPerWeek) {
  return (Array.isArray(dayKeys) ? dayKeys : []).filter((dateKey) => {
    const date = parseDateValue(dateKey);
    return date ? isWorkingDay(date, workingDaysPerWeek) : false;
  }).length;
}

export function holidayCounts(holiday) {
  return !holiday?.approvalRequired;
}

/*
  Minuten pro Ferientag: Snapshot → total_hours auf die Arbeitstage verteilt →
  Tagessoll. Ein offener Antrag zählt nicht.
*/
export function getHolidayPlannedMinutesPerDay(holiday, workingDaysPerWeek, fallbackDailyTargetHours) {
  const serverMinutes = toFiniteNumber(holiday?.plannedMinutesPerDay);
  if (serverMinutes !== null) return Math.max(0, Math.round(serverMinutes));

  if (!holidayCounts(holiday)) return 0;

  const snapshotMinutes = minutesFromHours(holiday?.hoursPerDay);
  if (snapshotMinutes > 0) return snapshotMinutes;

  const totalHours = toFiniteNumber(holiday?.totalHours);
  if (totalHours !== null && totalHours > 0) {
    const fullWorkingDayCount = countWorkingDayKeys(getUserHolidayDayKeys(holiday), workingDaysPerWeek);
    if (fullWorkingDayCount > 0) return minutesFromHours(totalHours / fullWorkingDayCount);
  }

  return minutesFromHours(fallbackDailyTargetHours);
}

/*
  Tage, an denen eine genehmigte Ferienbuchung greift. Eine Alt-Absenz "Ferien"
  am selben Tag zählt dann nicht mehr mit, sonst stünde dieselbe Person doppelt
  in der Summe.
*/
function buildHolidayDayKeys({ holidays, visibleDaysByKey, workingDaysPerWeek }) {
  return (Array.isArray(holidays) ? holidays : []).reduce((accumulator, holiday) => {
    if (!holidayCounts(holiday)) return accumulator;

    const employeeId = String(holiday?.userId || "");
    if (!employeeId) return accumulator;

    getVisibleWorkingDayKeys(getUserHolidayDayKeys(holiday), visibleDaysByKey, workingDaysPerWeek).forEach((dateKey) => {
      accumulator.add(`${employeeId}::${dateKey}`);
    });
    return accumulator;
  }, new Set());
}

function buildUserHolidayMinutesByEmployeeId({ holidays, employeesById, visibleDaysByKey, workingDaysPerWeek }) {
  return (Array.isArray(holidays) ? holidays : []).reduce((accumulator, holiday) => {
    const employeeId = String(holiday?.userId || "");
    if (!employeeId || !holidayCounts(holiday)) return accumulator;

    const visibleWorkingDayCount = getVisibleWorkingDayKeys(
      getUserHolidayDayKeys(holiday),
      visibleDaysByKey,
      workingDaysPerWeek
    ).length;
    if (!visibleWorkingDayCount) return accumulator;

    const dailyTargetHours = getEmployeeDailyTargetHours(employeesById.get(employeeId), workingDaysPerWeek);
    const minutesPerDay = getHolidayPlannedMinutesPerDay(holiday, workingDaysPerWeek, dailyTargetHours);
    accumulator[employeeId] = (accumulator[employeeId] || 0) + (visibleWorkingDayCount * minutesPerDay);
    return accumulator;
  }, {});
}

function formatHours(value) {
  const normalizedValue = Number(value || 0);
  const formatter = new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(normalizedValue) ? 0 : 1,
  });
  return `${formatter.format(normalizedValue)}h`;
}

function formatVacationDays(value) {
  const count = Math.max(0, Number(value || 0));
  return count === 1 ? "1 Ferientag" : `${count} Ferientage`;
}

function getProgressState(plannedHours, targetHours) {
  const planned = Number(plannedHours || 0);
  const target = Number(targetHours || 0);

  if (!target || target <= 0) {
    return {
      tone: "neutral",
      progressDegrees: 0,
      overageDegrees: 0,
    };
  }

  const delta = planned - target;
  const ratio = planned / target;

  if (Math.abs(delta) <= WORKLOAD_HOUR_EPSILON) {
    return {
      tone: "complete",
      progressDegrees: FULL_CIRCLE_DEGREES,
      overageDegrees: 0,
    };
  }

  if (delta < 0) {
    return {
      tone: "normal",
      progressDegrees: Math.max(0, Math.min(FULL_CIRCLE_DEGREES, ratio * FULL_CIRCLE_DEGREES)),
      overageDegrees: 0,
    };
  }

  return {
    tone: "over",
    progressDegrees: FULL_CIRCLE_DEGREES,
    overageDegrees: Math.max(0, Math.min(FULL_CIRCLE_DEGREES, (ratio - 1) * FULL_CIRCLE_DEGREES)),
  };
}

export function buildEmployeeWorkloadMap({
  employees,
  shifts,
  days,
  workingDaysPerWeek,
  absenceOptions = [],
  vacationAbsenceIds = new Set(),
  userHolidays = [],
}) {
  const visibleWorkingDayCount = countWorkingDays(days, workingDaysPerWeek);
  const visibleDaysByKey = (Array.isArray(days) ? days : []).reduce((accumulator, day) => {
    const key = String(day?.key || "").trim();
    if (key) accumulator.set(key, day);
    return accumulator;
  }, new Map());
  const visibleDayKeys = new Set(visibleDaysByKey.keys());
  const normalizedWorkingDaysPerWeek = Math.max(
    1,
    Math.min(7, Number(workingDaysPerWeek || DEFAULT_WORKING_DAYS_PER_WEEK))
  );
  const absenceOptionsById = buildAbsenceOptionsById(absenceOptions);
  const employeesById = (Array.isArray(employees) ? employees : []).reduce((accumulator, employee) => {
    const employeeId = String(employee?.id || "");
    if (employeeId) accumulator.set(employeeId, employee);
    return accumulator;
  }, new Map());
  const holidayDayKeys = buildHolidayDayKeys({
    holidays: userHolidays,
    visibleDaysByKey,
    workingDaysPerWeek: normalizedWorkingDaysPerWeek,
  });

  const plannedMinutesByEmployeeId = (Array.isArray(shifts) ? shifts : []).reduce((accumulator, shift) => {
    const employeeId = String(shift?.employeeId || "");
    if (!employeeId) return accumulator;
    const dailyTargetHours = getEmployeeDailyTargetHours(employeesById.get(employeeId), normalizedWorkingDaysPerWeek);
    // Nur Alt-Absenzen vom Typ Ferien weichen einer Ferienbuchung; jede andere
    // Absenz darf am selben Tag zusätzlich stehen.
    const skipDayKeys = isVacationShift(shift, vacationAbsenceIds)
      ? new Set(
          Array.from(holidayDayKeys)
            .filter((key) => key.startsWith(`${employeeId}::`))
            .map((key) => key.split("::")[1])
        )
      : null;
    accumulator[employeeId] =
      (accumulator[employeeId] || 0) +
      getEntryPlannedMinutes(
        shift,
        absenceOptionsById,
        visibleDaysByKey,
        normalizedWorkingDaysPerWeek,
        dailyTargetHours,
        skipDayKeys
      );
    return accumulator;
  }, {});
  const userHolidayMinutesByEmployeeId = buildUserHolidayMinutesByEmployeeId({
    holidays: userHolidays,
    employeesById,
    visibleDaysByKey,
    workingDaysPerWeek: normalizedWorkingDaysPerWeek,
  });
  const vacationDayKeysByEmployeeId = (Array.isArray(shifts) ? shifts : []).reduce((accumulator, shift) => {
    const employeeId = String(shift?.employeeId || "");
    if (!employeeId || !isVacationShift(shift, vacationAbsenceIds)) return accumulator;

    if (!accumulator[employeeId]) accumulator[employeeId] = new Set();
    getShiftDayKeys(shift).forEach((dateKey) => {
      if (!visibleDayKeys.has(dateKey)) return;
      const visibleDay = visibleDaysByKey.get(dateKey);
      const visibleDate = parseDateValue(visibleDay?.date || dateKey);
      if (visibleDate && !isWorkingDay(visibleDate, workingDaysPerWeek)) return;
      accumulator[employeeId].add(dateKey);
    });
    return accumulator;
  }, {});

  return (Array.isArray(employees) ? employees : []).reduce((accumulator, employee) => {
    const employeeId = String(employee?.id || "");
    if (!employeeId) return accumulator;

    const targetHours = getEmployeeDailyTargetHours(employee, normalizedWorkingDaysPerWeek) * visibleWorkingDayCount;
    const plannedHours = minutesToHours(
      (plannedMinutesByEmployeeId[employeeId] || 0) + (userHolidayMinutesByEmployeeId[employeeId] || 0)
    );
    const userHolidayDays = countUserHolidayDays({ holidays: userHolidays, employeeId, days, workingDaysPerWeek });
    // Vereinigung statt Maximum: Alt-Absenz "Ferien" und Ferienbuchung können
    // disjunkte Tage abdecken (1.-2. plus 15.-19. sind 7 Tage, nicht 5). Ein
    // Maximum stimmt nur, solange die eine Menge in der anderen liegt — bei
    // Überlappung zählt weiterhin die Ferienbuchung, der Alt-Tag fällt weg.
    const uncoveredLegacyVacationDays = Array.from(
      vacationDayKeysByEmployeeId[employeeId] || []
    ).filter((dateKey) => !holidayDayKeys.has(`${employeeId}::${dateKey}`)).length;
    const vacationDays = uncoveredLegacyVacationDays + userHolidayDays;
    const progress = getProgressState(plannedHours, targetHours);

    accumulator[employeeId] = {
      plannedHours,
      targetHours,
      vacationDays,
      label: `${formatHours(plannedHours)} / ${formatHours(targetHours)}`,
      vacationLabel: formatVacationDays(vacationDays),
      tone: progress.tone,
      progressDegrees: progress.progressDegrees,
      overageDegrees: progress.overageDegrees,
    };
    return accumulator;
  }, {});
}
